import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { getProspectSearchWorkspace } from "@/app/actions/prospect-search/workspace/action";
import { buildClientProspectsQueryKey } from "@/app/hooks/fetchs/prospect-search/useFetchClientProspects";
import { buildImportedBaseStoredKey } from "@/app/hooks/fetchs/prospect-search/useFetchImportedBase";
import { ProspectSearchWorkspace } from "@/app/types/prospect/ProspectSearchWorkspace";
import { UProspectFilter } from "@/app/utils/UProspectFilter";
import { QueryKey } from "@/app/vars/QueryKeys";

type Options = {
  avatarId?: number;
  enabled?: boolean;
};

export function seedProspectSearchWorkspace(
  queryClient: QueryClient,
  avatarId: number,
  workspace: ProspectSearchWorkspace,
) {
  queryClient.setQueryData([QueryKey.PROSPECT_SEARCH_SUMMARY, avatarId], workspace.summary);
  queryClient.setQueryData([QueryKey.PROSPECTING_STATS, avatarId], workspace.stats);
  queryClient.setQueryData(
    [QueryKey.SOCIAL_IMPORTED_PROFILE_COUNTS, avatarId],
    workspace.importedCounts,
  );
  queryClient.setQueryData(buildImportedBaseStoredKey(avatarId), workspace.importedBase);
  queryClient.setQueryData(
    buildClientProspectsQueryKey(
      avatarId,
      workspace.prospectTab,
      workspace.prospectActionFilter,
      1,
    ),
    workspace.prospects,
  );
}

export function useFetchProspectSearchWorkspace(options: Options) {
  const { avatarId, enabled = true } = options;
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [QueryKey.PROSPECT_SEARCH_WORKSPACE, avatarId],
    queryFn: async () => {
      const workspace = await getProspectSearchWorkspace(
        avatarId!,
        UProspectFilter.readStoredTab(),
        UProspectFilter.readStoredActionFilter(),
      );

      seedProspectSearchWorkspace(queryClient, avatarId!, workspace);

      return workspace;
    },
    enabled: enabled && !!avatarId,
    staleTime: Infinity,
  });
}
